import { AlertConditionType } from '../types/alerts';
import {
  ApiKey,
  CircuitBreakerStatus,
  DeviceCode,
  DeviceCodeResponse,
  IncomingEvent,
  Issue,
  OrganizationInvitation,
  PaginationInfo,
  RequestLog,
  Secret,
  WebhookDelivery,
  WebhookDeliveryAttempt,
} from '../types/index';
import { HttpError } from './fetchClient';
import {
  CreateDebugEndpointRequest,
  CreateShareRequest,
  DebugEndpoint,
  DebugEndpointsResponse,
  DebugEndpointResponse,
  DebugRequestsResponse,
  DebugRequestDetail,
  DebugRequestForwardsResponse,
  DebugRequestForwardDetail,
  ForwardDebugRequestPayload,
  ForwardDebugRequestResponse,
  SharedDebugRequest,
  SharedDebugRequestResponse,
  SharedDebugRequestsResponse,
  SharedRequestFullData,
  SharedRequestFullResponse,
  SharedRequestProtectedResponse,
  UpdateDebugEndpointRequest,
} from '../types/debug-endpoints';
import { Transformation } from '../types/bridge';
import { TransformationListResponse } from '../types/transformation';
import { fetchClient } from './fetchClient';

export interface Organization {
  id: string;
  name: string;
  updated_at?: string;
  created_at?: string;
  signing_secret_prefix?: string;
}

export interface NotificationPreferences {
  id: string;
  organization_id: string;
  issue_notifications_enabled: boolean;
  notification_cooldown_minutes: number;
  stats_notifications_enabled: boolean;
  slack_issue_notifications_enabled: boolean;
  slack_stats_notifications_enabled: boolean;
  slack_webhook_url?: string | null;
  last_issue_notification_at?: string | null;
  last_stats_notification_at?: string | null;
  updated_at: string;
}

export interface UpdateNotificationPreferencesRequest {
  issue_notifications_enabled?: boolean;
  stats_notifications_enabled?: boolean;
  slack_issue_notifications_enabled?: boolean;
  slack_stats_notifications_enabled?: boolean;
  slack_webhook_url?: string | null;
}

export interface ScheduleAlert {
  id: string;
  schedule_id: string;
  name: string;
  description?: string;
  alert_type: AlertConditionType;
  config: Record<string, string>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateScheduleAlertRequest {
  name: string;
  description?: string;
  alert_type: AlertConditionType;
  config: Record<string, string>;
  is_active: boolean;
}

export const getAlertTypeLabel = (type: AlertConditionType): string => {
  switch (type) {
    case 'body_contains':
      return 'Body Contains';
    case 'status_code_range':
      return 'Status Code Range';
    default:
      return type;
  }
};

export const getAlertConfigDescription = (
  type: AlertConditionType,
  config: Record<string, string>
): string => {
  switch (type) {
    case 'body_contains':
      return `Contains "${config.value}"`;
    case 'status_code_range':
      return `Status ${config.min}-${config.max}`;
    default:
      return '';
  }
};

export const fetchOrganizations = async (): Promise<Organization[]> => {
  return fetchClient<Organization[]>('/api/v1/organizations');
};

export const createScheduleAlert = async (
  scheduleId: string,
  data: CreateScheduleAlertRequest
): Promise<ScheduleAlert> => {
  return fetchClient<ScheduleAlert>(`/api/v1/schedules/${scheduleId}/alerts`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const listScheduleAlerts = async (
  scheduleId: string
): Promise<ScheduleAlert[]> => {
  return fetchClient<ScheduleAlert[]>(`/api/v1/schedules/${scheduleId}/alerts`);
};

export const getScheduleAlert = async (
  scheduleId: string,
  alertId: string
): Promise<ScheduleAlert> => {
  return fetchClient<ScheduleAlert>(
    `/api/v1/schedules/${scheduleId}/alerts/${alertId}`
  );
};

export const updateScheduleAlert = async (
  scheduleId: string,
  alertId: string,
  data: CreateScheduleAlertRequest
): Promise<void> => {
  await fetchClient<void>(`/api/v1/schedules/${scheduleId}/alerts/${alertId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteScheduleAlert = async (
  scheduleId: string,
  alertId: string
): Promise<void> => {
  await fetchClient(`/api/v1/schedules/${scheduleId}/alerts/${alertId}`, {
    method: 'DELETE',
  });
};

/**
 * Fetches incoming webhook requests
 * @param page Page number for pagination
 * @param pageSize Number of items per page
 * @returns Promise with request logs and pagination info
 */
export interface RequestLogsResponse {
  data: RequestLog[];
  pagination: PaginationInfo;
}

export interface IncomingEventsResponse {
  items: IncomingEvent[];
  pagination: PaginationInfo;
}

export const fetchRequests = async (
  page: number = 1,
  pageSize: number = 10,
  methods?: string[],
  id?: string,
  bridge_id?: string,
  source_id?: string
): Promise<IncomingEventsResponse> => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  if (methods && methods.length > 0) {
    methods.forEach((method) => params.append('method[]', method));
  }

  if (id && id.trim() !== '') {
    params.append('id', id.trim());
  }

  if (bridge_id && bridge_id.trim() !== '') {
    params.append('bridge_id', bridge_id.trim());
  }

  if (source_id && source_id.trim() !== '') {
    params.append('source_id', source_id.trim());
  }

  return fetchClient<IncomingEventsResponse>(
    `/api/v1/requests?${params.toString()}`
  );
};

// Define a basic type for retry attempts - refine if needed
export interface RetryAttempt {
  id: string;
  timestamp: string; // Or number, depending on API
  status: string;
  // Add other relevant fields based on API response
}

/**
 * Fetches retry attempts for a specific incoming request
 * @param sourceId The ID of the source
 * @param requestId The ID of the incoming request
 * @returns Promise with an array of retry attempts
 */
// Define a type for the response containing request details and retries
export interface RequestDetailsWithRetries extends IncomingEvent {
  retries: RetryAttempt[];
  // Add other request-specific fields if the endpoint returns more than just IncomingEvent fields
}

/**
 * Fetches retry attempts for a specific incoming request
 * @param requestId The ID of the incoming request
 * @returns Promise with request details including an array of retry attempts
 */
export const fetchRetries = async (
  requestId: string
): Promise<RequestDetailsWithRetries> => {
  return fetchClient<RequestDetailsWithRetries>(
    `/api/v1/requests/${requestId}`
  );
};

export interface WebhookDeliveriesResponse {
  items: WebhookDelivery[];
  pagination: PaginationInfo;
}

/**
 * Fetches outgoing webhook deliveries
 * @param page Page number for pagination
 * @param pageSize Number of items per page
 * @param statuses Optional array of statuses to filter by
 * @returns Promise with webhook deliveries and pagination info
 */
export const fetchWebhookDeliveries = async (
  page: number = 1,
  pageSize: number = 10,
  statuses?: string[],
  bridge_id?: string // Changed from id to bridge_id parameter
): Promise<WebhookDeliveriesResponse> => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  if (statuses && statuses.length > 0) {
    statuses.forEach((status) => params.append('status[]', status));
  }

  if (bridge_id && bridge_id.trim() !== '') {
    // Changed from id to bridge_id parameter handling
    params.append('bridge_id', bridge_id.trim());
  }

  return fetchClient<WebhookDeliveriesResponse>(
    `/api/v1/webhook-deliveries?${params.toString()}`
  );
};

export interface DebugEndpointStats {
  endpoint_id: string;
  endpoint_name: string;
  endpoint_slug: string;
  request_count: number;
  last_received_at: string | null;
}

export interface DashboardMetrics {
  active_bridges: number;
  active_sources: number;
  active_destinations: number;
  total_source_incoming_requests: number;
  total_debug_webhook_requests: number;
  source_incoming_requests_by_time: Array<{
    timestamp: string;
    count: number;
  }>;
  debug_webhook_requests_by_time: Array<{
    timestamp: string;
    count: number;
  }>;
  total_webhook_deliveries: number;
  webhook_deliveries_by_time: Array<{
    timestamp: string;
    statuses: {
      failed?: number;
      pending?: number;
      [key: string]: number | undefined;
    };
  }>;
  interval: string;
  top_destinations?: Array<{
    id: string;
    name: string;
    url: string;
    http_method: string | null;
    webhook_count: number;
    percentage: number;
    avg_response_time_ms: string;
  }>;
  top_sources?: Array<{
    id: string;
    name: string;
    webhook_count: number;
    percentage: number;
  }>;
  debug_webhook_requests_by_endpoint: DebugEndpointStats[];
}

export const fetchDashboardMetrics = async (
  start_date?: string,
  end_date?: string
): Promise<DashboardMetrics> => {
  const params = new URLSearchParams();

  if (start_date) {
    params.append('start_date', start_date);
  }

  if (end_date) {
    params.append('end_date', end_date);
  }

  const queryString = params.toString();
  const url = `/api/v1/metrics${queryString ? `?${queryString}` : ''}`;

  return fetchClient<DashboardMetrics>(url);
};

export interface BridgeAttention {
  bridge_id: string;
  bridge_name: string;
  destination_id: string;
  destination_name: string;
  destination_url: string;
  failed_deliveries: number;
  retry_percentage: number;
}

export const fetchBridgesNeedingAttention = async (): Promise<
  BridgeAttention[]
> => {
  return fetchClient<BridgeAttention[]>('/api/v1/metrics/attention-bridges');
};

export interface WebhookDeliveryWithAttempts {
  delivery: WebhookDelivery;
  attempts: WebhookDeliveryAttempt[];
}

/**
 * Fetches attempts for a specific webhook delivery
 * @param deliveryId The ID of the webhook delivery
 * @returns Promise with webhook delivery and its attempts
 */
export const fetchWebhookDeliveryAttempts = async (
  deliveryId: string
): Promise<WebhookDeliveryWithAttempts> => {
  return fetchClient<WebhookDeliveryWithAttempts>(
    `/api/v1/webhook-deliveries/${deliveryId}/attempts`
  );
};

/**
 * Fetches details for a specific webhook delivery attempt
 * @param attemptId The ID of the webhook delivery attempt
 * @returns Promise with webhook delivery attempt details
 */
export const fetchWebhookDeliveryAttemptDetails = async (
  attemptId: string
): Promise<WebhookDeliveryAttempt> => {
  return fetchClient<WebhookDeliveryAttempt>(
    `/api/v1/webhook-deliveries/attempts/${attemptId}`
  );
};

/**
 * Manually retry a webhook delivery
 * @param deliveryId The ID of the webhook delivery to retry
 * @returns Promise that resolves when the retry is triggered
 */
export const retryWebhookDelivery = async (deliveryId: string): Promise<void> => {
  return fetchClient<void>(`/api/v1/webhook-deliveries/${deliveryId}/retry`, {
    method: 'POST',
  });
};

/**
 * Fetches all debug endpoints
 * @returns Promise with an array of debug endpoints
 */
export const fetchDebugEndpoints = async (): Promise<DebugEndpoint[]> => {
  const response = await fetchClient<DebugEndpointsResponse>('/api/v1/debug-endpoints');
  return response.data;
};

/**
 * Creates a new debug endpoint
 * @param data The debug endpoint data
 * @returns Promise with the created debug endpoint
 */
export const createDebugEndpoint = async (
  data: CreateDebugEndpointRequest
): Promise<DebugEndpoint> => {
  const response = await fetchClient<DebugEndpointResponse>('/api/v1/debug-endpoints', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.data;
};

/**
 * Fetches a specific debug endpoint
 * @param id The ID of the debug endpoint
 * @returns Promise with the debug endpoint
 */
export const fetchDebugEndpoint = async (id: string): Promise<DebugEndpoint> => {
  const response = await fetchClient<DebugEndpointResponse>(`/api/v1/debug-endpoints/${id}`);
  return response.data;
};

/**
 * Updates a debug endpoint
 * @param id The ID of the debug endpoint
 * @param data The update data
 * @returns Promise with the updated debug endpoint
 */
export const updateDebugEndpoint = async (
  id: string,
  data: UpdateDebugEndpointRequest
): Promise<DebugEndpoint> => {
  const response = await fetchClient<DebugEndpointResponse>(`/api/v1/debug-endpoints/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.data;
};

/**
 * Deletes a debug endpoint
 * @param id The ID of the debug endpoint
 * @returns Promise that resolves when the endpoint is deleted
 */
export const deleteDebugEndpoint = async (id: string): Promise<void> => {
  await fetchClient(`/api/v1/debug-endpoints/${id}`, {
    method: 'DELETE',
  });
};

/**
 * Fetches debug requests for a specific endpoint
 * @param id The ID of the debug endpoint
 * @param page Page number for pagination
 * @param pageSize Number of items per page
 * @returns Promise with debug requests and pagination info
 */
export const fetchDebugRequests = async (
  id: string,
  page: number = 1,
  pageSize: number = 50
): Promise<DebugRequestsResponse> => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  return fetchClient<DebugRequestsResponse>(
    `/api/v1/debug-endpoints/${id}/requests?${params.toString()}`
  );
};

/**
 * Fetches full details of a specific debug request
 * @param endpointId The ID of the debug endpoint
 * @param requestId The ID of the debug request
 * @returns Promise with the full debug request details including body
 */
export const fetchDebugRequestDetail = async (
  endpointId: string,
  requestId: string
): Promise<DebugRequestDetail> => {
  const response = await fetchClient<{ data: DebugRequestDetail }>(
    `/api/v1/debug-endpoints/${endpointId}/requests/${requestId}`
  );
  return response.data;
};

/**
 * Lists forward attempts for a captured debug request
 * @param endpointId The debug endpoint ID
 * @param requestId The debug request ID
 * @param page Pagination page
 * @param pageSize Items per page
 */
export const fetchDebugRequestForwards = async (
  endpointId: string,
  requestId: string,
  page: number = 1,
  pageSize: number = 10
): Promise<DebugRequestForwardsResponse> => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  return fetchClient<DebugRequestForwardsResponse>(
    `/api/v1/debug-endpoints/${endpointId}/requests/${requestId}/forwards?${params.toString()}`
  );
};

/**
 * Fetches the full detail for a specific forward attempt
 * @param forwardId The forward ID
 */
export const fetchForwardDetail = async (
  forwardId: string
): Promise<DebugRequestForwardDetail> => {
  const response = await fetchClient<{ data: DebugRequestForwardDetail }>(
    `/api/v1/forwards/${forwardId}`
  );
  return response.data;
};

/**
 * Forwards a captured debug request to a new target URL
 * @param endpointId The ID of the debug endpoint
 * @param requestId The ID of the debug request
 * @param data Forward payload containing target URL and optional method override
 * @returns Promise resolving with forward metadata
 */
export const forwardDebugRequest = async (
  endpointId: string,
  requestId: string,
  data: ForwardDebugRequestPayload
): Promise<ForwardDebugRequestResponse> => {
  return fetchClient<ForwardDebugRequestResponse>(
    `/api/v1/debug-endpoints/${endpointId}/requests/${requestId}/forward`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
};

/**
 * Fetches circuit breaker statuses for all destinations
 * @returns Promise with array of circuit breaker statuses
 */
export const fetchCircuitBreakers = async (): Promise<CircuitBreakerStatus[]> => {
  const response = await fetchClient<{ data: CircuitBreakerStatus[] }>(
    '/api/v1/circuit-breakers'
  );
  return response.data;
};

/**
 * Resets a circuit breaker for a specific destination
 * @param destinationId The ID of the destination
 * @returns Promise with success status
 */
export const resetCircuitBreaker = async (destinationId: string): Promise<{ success: boolean }> => {
  return fetchClient<{ success: boolean }>(
    `/api/v1/circuit-breakers/${destinationId}/reset`,
    {
      method: 'POST',
    }
  );
};

export interface IssuesResponse {
  items: Issue[];
  pagination: PaginationInfo;
}

/**
 * Fetches issues with pagination and filtering
 * @param page Page number for pagination
 * @param pageSize Number of items per page
 * @param status Optional status filter (single or multiple)
 * @returns Promise with issues and pagination info
 */
export const fetchIssues = async (
  page: number = 1,
  pageSize: number = 10,
  status?: string | string[]
): Promise<IssuesResponse> => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  if (status) {
    if (Array.isArray(status)) {
      params.append('status', status.join(','));
    } else {
      params.append('status', status);
    }
  }

  return fetchClient<IssuesResponse>(
    `/api/v1/issues?${params.toString()}`
  );
};

/**
 * Fetches specific issue details
 * @param issueId The ID of the issue
 * @returns Promise with issue details
 */
export const fetchIssue = async (issueId: string): Promise<Issue> => {
  return fetchClient<Issue>(
    `/api/v1/issues/${issueId}`
  );
};

/**
 * Updates issue status
 * @param issueId The ID of the issue
 * @param status The new status
 * @returns Promise with updated issue
 */
export const updateIssueStatus = async (
  issueId: string,
  status: 'open' | 'work_in_progress' | 'closed' | 'muted'
): Promise<Issue> => {
  return fetchClient<Issue>(
    `/api/v1/issues/${issueId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }
  );
};

/**
 * Fetches invitation details by token
 * @param token The invitation token
 * @returns Promise with invitation details
 */
export const fetchInvitation = async (token: string): Promise<OrganizationInvitation> => {
  const response = await fetchClient<{ data: OrganizationInvitation }>(
    `/api/v1/invitations/${token}`
  );
  return response.data;
};

/**
 * Accepts an organization invitation
 * @param token The invitation token
 * @returns Promise with success status
 */
export const acceptInvitation = async (token: string): Promise<{ success: boolean; organization_id: string }> => {
  return fetchClient<{ success: boolean; organization_id: string }>(
    `/api/v1/invitations/${token}/accept`,
    {
      method: 'POST',
    }
  );
};

/**
 * Creates a new API key
 * @param organizationId The organization ID
 * @param name The name for the API key
 * @returns Promise with the created API key (including the key value)
 */
export const createApiKey = async (
  organizationId: string,
  name: string
): Promise<ApiKey> => {
  return fetchClient<ApiKey>(
    `/api/v1/organizations/${organizationId}/api-keys`,
    {
      method: 'POST',
      body: JSON.stringify({ name }),
    }
  );
};

/**
 * Fetches all API keys for an organization
 * @param organizationId The organization ID
 * @returns Promise with array of API keys (without key values)
 */
export const fetchApiKeys = async (
  organizationId: string
): Promise<ApiKey[]> => {
  return fetchClient<ApiKey[]>(
    `/api/v1/organizations/${organizationId}/api-keys`
  );
};

/**
 * Revokes an API key
 * @param organizationId The organization ID
 * @param keyId The API key ID to revoke
 * @returns Promise with success message
 */
export const revokeApiKey = async (
  organizationId: string,
  keyId: string
): Promise<{ message: string }> => {
  return fetchClient<{ message: string }>(
    `/api/v1/organizations/${organizationId}/api-keys/${keyId}`,
    {
      method: 'DELETE',
    }
  );
};

export interface SigningSecretResponse {
  signing_secret: string;
  created_at: string;
}

/**
 * Generates initial signing secret for an organization
 * @param organizationId The organization ID
 * @returns Promise with signing secret details
 */
export const generateSigningSecret = async (
  organizationId: string
): Promise<SigningSecretResponse> => {
  return fetchClient<SigningSecretResponse>(
    `/api/v1/organizations/${organizationId}/signing_secret`,
    {
      method: 'POST',
    }
  );
};

/**
 * Rolls (regenerates) the signing secret for an organization
 * @param organizationId The organization ID
 * @returns Promise with new signing secret details
 */
export const rollSigningSecret = async (
  organizationId: string
): Promise<SigningSecretResponse> => {
  return fetchClient<SigningSecretResponse>(
    `/api/v1/organizations/${organizationId}/signing_secret/roll`,
    {
      method: 'POST',
    }
  );
};

// Secrets API functions

export interface SecretListResponse {
  data: Secret[];
}

export interface SecretResponse {
  data: Secret;
}

export interface SecretValueResponse {
  id: string;
  name: string;
  value: string;
  decrypted_at: string;
}

export interface CreateSecretRequest {
  secret: {
    name: string;
    value: string;
    description?: string;
  };
}

export interface UpdateSecretRequest {
  secret: {
    value?: string;
    description?: string;
  };
}

/**
 * Fetches all secrets
 * @returns Promise with list of secrets
 */
export const fetchSecrets = async (): Promise<Secret[]> => {
  const response = await fetchClient<SecretListResponse>('/api/v1/secrets');
  return response.data;
};

/**
 * Fetches a single secret by ID (metadata only)
 * @param id The secret ID
 * @returns Promise with secret details
 */
export const fetchSecret = async (id: string): Promise<Secret> => {
  const response = await fetchClient<SecretResponse>(`/api/v1/secrets/${id}`);
  return response.data;
};

/**
 * Fetches a secret with decrypted value
 * @param id The secret ID
 * @returns Promise with secret value
 */
export const fetchSecretValue = async (id: string): Promise<SecretValueResponse> => {
  return fetchClient<SecretValueResponse>(`/api/v1/secrets/${id}/value`);
};

/**
 * Creates a new secret
 * @param data The secret data
 * @returns Promise with created secret
 */
export const createSecret = async (data: CreateSecretRequest): Promise<Secret> => {
  return fetchClient<Secret>('/api/v1/secrets', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Updates an existing secret
 * @param id The secret ID
 * @param data The secret update data
 * @returns Promise with updated secret
 */
export const updateSecret = async (id: string, data: UpdateSecretRequest): Promise<Secret> => {
  return fetchClient<Secret>(`/api/v1/secrets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Deletes a secret
 * @param id The secret ID
 * @returns Promise<void>
 */
export const deleteSecret = async (id: string): Promise<void> => {
  await fetchClient(`/api/v1/secrets/${id}`, {
    method: 'DELETE',
  });
};

// Transformation API functions

export interface TransformationCreateRequest {
  transformation: {
    name: string;
    description?: string;
    code: string;
  };
}

export interface TransformationUpdateRequest {
  transformation: {
    name?: string;
    description?: string;
    code?: string;
  };
}

export interface TransformationResponse {
  data: Transformation;
}

export interface TransformationsListResponse {
  data: Transformation[];
}

/**
 * Fetches all transformations
 * @returns Promise with list of transformations
 */
export const fetchTransformations = async (): Promise<Transformation[]> => {
  const response = await fetchClient<TransformationListResponse>('/api/v1/transformations');
  return response.data;
};

/**
 * Fetches a single transformation by ID
 * @param id The transformation ID
 * @returns Promise with transformation details
 */
export const fetchTransformation = async (id: string): Promise<Transformation> => {
  const response = await fetchClient<TransformationResponse>(`/api/v1/transformations/${id}`);
  return response.data;
};

/**
 * Creates a new transformation
 * @param data The transformation data
 * @returns Promise with created transformation
 */
export const createTransformation = async (data: TransformationCreateRequest): Promise<Transformation> => {
  const response = await fetchClient<TransformationResponse>('/api/v1/transformations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.data;
};

/**
 * Updates an existing transformation
 * @param id The transformation ID
 * @param data The transformation update data
 * @returns Promise with updated transformation
 */
export const updateTransformation = async (id: string, data: TransformationUpdateRequest): Promise<Transformation> => {
  const response = await fetchClient<TransformationResponse>(`/api/v1/transformations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return response.data;
};

/**
 * Deletes a transformation
 * @param id The transformation ID
 * @returns Promise<void>
 */
export const deleteTransformation = async (id: string): Promise<void> => {
  await fetchClient(`/api/v1/transformations/${id}`, {
    method: 'DELETE',
  });
};

// Test transformation API
export interface TestTransformationRequest {
  request: {
    headers?: Record<string, string>;
    body?: any;
    query?: string;
    parsed_query?: Record<string, string>;
  };
  transformation: {
    code: string;
  };
}

export interface TestTransformationResponse {
  data: {
    result: any;
  };
}

export interface GenerateTransformationRequest {
  prompt: string;
  context?: Record<string, unknown>;
}

export interface GenerateTransformationResponse {
  data: GenerateTransformationResult;
}

export interface GenerateTransformationResult {
  code: string;
  name?: string;
  description?: string;
  summary?: string;
}

/**
 * Test a transformation with sample request data
 * @param data - Test transformation request
 * @returns Test result
 */
export const testTransformation = async (data: TestTransformationRequest): Promise<any> => {
  const response = await fetchClient<TestTransformationResponse>('/api/v1/transformations/test', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.data.result;
};

/**
 * Generates transformation code suggestions based on a natural language prompt
 * @param data - Prompt data for generation
 * @returns Generated transformation details
 */
export const generateTransformationCode = async (
  data: GenerateTransformationRequest,
): Promise<GenerateTransformationResult> => {
  const response = await fetchClient<GenerateTransformationResponse>(
    '/api/v1/transformations/generate',
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );

  return response.data;
};

// Transformation Logs API
import { 
  TransformationLogsResponse, 
  TransformationLogDetailResponse,
  TransformationLogStatus 
} from '../types/transformation-logs';

export interface TransformationLogsParams {
  page?: number;
  page_size?: number;
  status?: TransformationLogStatus;
}

/**
 * Fetches transformation logs for a specific transformation
 * @param transformationId The transformation ID
 * @param params Query parameters
 * @returns Promise with transformation logs and pagination
 */
export const fetchTransformationLogs = async (
  transformationId: string,
  params?: TransformationLogsParams
): Promise<TransformationLogsResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
  if (params?.status) queryParams.append('status', params.status);
  
  const queryString = queryParams.toString();
  const url = `/api/v1/transformations/${transformationId}/logs${queryString ? `?${queryString}` : ''}`;
  
  return fetchClient<TransformationLogsResponse>(url);
};

/**
 * Fetches details for a specific transformation log
 * @param transformationId The transformation ID
 * @param logId The log ID
 * @param includeData Whether to include input/output data
 * @returns Promise with transformation log details
 */
export const fetchTransformationLogDetail = async (
  transformationId: string,
  logId: string,
  includeData: boolean = false
): Promise<TransformationLogDetailResponse> => {
  const url = `/api/v1/transformations/${transformationId}/logs/${logId}${includeData ? '?include_data=true' : ''}`;
  return fetchClient<TransformationLogDetailResponse>(url);
};

// Notification Preferences API functions

/**
 * Updates notification preferences for an organization
 * @param organizationId The organization ID
 * @param preferences The notification preferences to update
 * @returns Promise with updated notification preferences
 */
export const updateNotificationPreferences = async (
  organizationId: string,
  preferences: UpdateNotificationPreferencesRequest
): Promise<NotificationPreferences> => {
  return fetchClient<NotificationPreferences>(
    `/api/v1/organizations/${organizationId}/notification_preferences`,
    {
      method: 'PATCH',
      body: JSON.stringify(preferences),
    }
  );
};

// Device Code API functions

/**
 * Verifies a device code using the provided user code
 * @param userCode The user code to verify
 * @returns Promise with the verified device code
 */
export const verifyDeviceCode = async (
  userCode: string
): Promise<DeviceCode> => {
  const response = await fetchClient<DeviceCodeResponse>(
    '/api/v1/auth/device/verify',
    {
      method: 'POST',
      body: JSON.stringify({ user_code: userCode }),
    }
  );
  return response.device_code;
};

/**
 * Fetches device code status by device code string
 * @param deviceCodeString The device code string
 * @returns Promise with device code details or null if not found
 */
export const fetchDeviceCode = async (
  deviceCodeString: string
): Promise<DeviceCode | null> => {
  try {
    return await fetchClient<DeviceCode>(
      `/api/v1/device?device_code=${deviceCodeString}`
    );
  } catch (error) {
    // If the device code is not found, return null instead of throwing
    if (error instanceof Error && error.message.includes('404')) {
      return null;
    }
    throw error;
  }
};

// AI Assistant API
export interface AISessionResponse {
  session_id: string;
}

export interface AIChatResponse {
  id: string;
  content: string;
  role: 'assistant' | 'system';
}

export const createAISession = async (): Promise<AISessionResponse> => {
  return fetchClient<AISessionResponse>('/api/v1/assistant/session', {
    method: 'POST',
  });
};

export const sendAIMessage = async (
  sessionId: string,
  message: string
): Promise<AIChatResponse> => {
  return fetchClient<AIChatResponse>(`/api/v1/assistant/${sessionId}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ content: message }),
  });
};

// Schedule API functions

export interface Schedule {
  id: string;
  name: string;
  cron_expression: string;
  status: 'active' | 'inactive';
  target_url: string;
  http_method: 'get' | 'post' | 'put' | 'patch' | 'delete';
  http_headers: Record<string, string>;
  http_body?: string;
  timeout_seconds: number;
  alert_on_failure: boolean;
  expected_status_codes: number[];
  expected_response_keyword?: string;
  last_run_at?: string;
  next_run_at?: string;
  last_run_status?: string;
  last_run_message?: string;
  inserted_at: string;
  updated_at: string;
}

export interface CreateScheduleRequest {
  name: string;
  cron_expression: string;
  status?: 'active' | 'inactive';
  target_url: string;
  http_method: 'get' | 'post' | 'put' | 'patch' | 'delete';
  http_headers?: Record<string, string>;
  http_body?: string;
  timeout_seconds?: number;
  alert_on_failure?: boolean;
  expected_status_codes?: number[];
  expected_response_keyword?: string;
}

export interface SchedulesListResponse {
  data: Schedule[];
}

export interface ScheduleResponse {
  data: Schedule;
}

/**
 * Fetches all schedules for the organization
 * @returns Promise with list of schedules
 */
export const fetchSchedules = async (): Promise<Schedule[]> => {
  const response = await fetchClient<SchedulesListResponse>('/api/v1/schedules');
  return response.data;
};

/**
 * Fetches a single schedule by ID
 * @param id The schedule ID
 * @returns Promise with schedule details
 */
export const fetchSchedule = async (id: string): Promise<Schedule> => {
  const response = await fetchClient<ScheduleResponse>(`/api/v1/schedules/${id}`);
  return response.data;
};

/**
 * Creates a new schedule
 * @param data The schedule data
 * @returns Promise with created schedule
 */
export const createSchedule = async (data: CreateScheduleRequest): Promise<Schedule> => {
  const response = await fetchClient<ScheduleResponse>('/api/v1/schedules', {
    method: 'POST',
    body: JSON.stringify({ schedule: data }),
  });
  return response.data;
};

/**
 * Updates an existing schedule
 * @param id The schedule ID
 * @param data The schedule update data
 * @returns Promise with updated schedule
 */
export const updateSchedule = async (
  id: string,
  data: Partial<CreateScheduleRequest>
): Promise<Schedule> => {
  const response = await fetchClient<ScheduleResponse>(`/api/v1/schedules/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ schedule: data }),
  });
  return response.data;
};

/**
 * Deletes a schedule
 * @param id The schedule ID
 * @returns Promise<void>
 */
export const deleteSchedule = async (id: string): Promise<void> => {
  await fetchClient(`/api/v1/schedules/${id}`, {
    method: 'DELETE',
  });
};

// Shared Request API Functions

/**
 * Creates a share for a debug request
 * @param debugRequestId The debug request ID
 * @param data Share configuration (expiration, password, include_forwards)
 * @returns Promise with created share details
 */
export const createShare = async (
  debugRequestId: string,
  data: CreateShareRequest
): Promise<SharedDebugRequest> => {
  const response = await fetchClient<SharedDebugRequestResponse>(
    `/api/v1/debug-requests/${debugRequestId}/share`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  return response.data;
};

/**
 * Lists all shares for a debug request
 * @param debugRequestId The debug request ID
 * @returns Promise with array of shares
 */
export const fetchRequestShares = async (
  debugRequestId: string
): Promise<SharedDebugRequest[]> => {
  const response = await fetchClient<SharedDebugRequestsResponse>(
    `/api/v1/debug-requests/${debugRequestId}/shares`
  );
  return response.data;
};

/**
 * Revokes/deletes a share
 * @param shareToken The share token to revoke
 * @returns Promise<void>
 */
export const revokeShare = async (shareToken: string): Promise<void> => {
  await fetchClient(`/api/v1/shared/r/${shareToken}`, {
    method: 'DELETE',
  });
};

/**
 * Gets a shared request by token (public endpoint)
 * @param shareToken The share token
 * @returns Promise with shared request data or protected info
 */
export const getSharedRequest = async (
  shareToken: string
): Promise<SharedRequestFullData | { protected: true; expires_at: string; include_forwards: boolean }> => {
  try {
    const response = await fetchClient<SharedRequestFullResponse | SharedRequestProtectedResponse>(
      `/api/v1/shared/r/${shareToken}`,
      {
        skipAuth: true, // Public endpoint
      }
    );
    return response.data;
  } catch (error) {
    // Check if this is a 401 error with password-protected share data
    if (error instanceof HttpError && error.status === 401) {
      // If the error contains protected share info, return it
      if (error.data?.data?.protected === true) {
        return error.data.data;
      }
    }
    // Re-throw other errors
    throw error;
  }
};

/**
 * Authenticates a password-protected share
 * @param shareToken The share token
 * @param password The password
 * @returns Promise with shared request data
 */
export const authenticateShare = async (
  shareToken: string,
  password: string
): Promise<SharedRequestFullData> => {
  const response = await fetchClient<SharedRequestFullResponse>(
    `/api/v1/shared/r/${shareToken}/authenticate`,
    {
      method: 'POST',
      body: JSON.stringify({ password }),
      skipAuth: true, // Public endpoint
    }
  );
  return response.data;
};
